import { CreatePostMutationVariables } from '__generated__/graphql/legacy/graphql';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { sanitizeAttachments } from 'helpers/postHelpers';
import useSpacesList from 'hooks/useSpacesList';
import { Space } from 'interfaces/space';
import { TFunction, withTranslation } from 'next-i18next';
import { EVENT_POST_TYPES, SUCCESS_TOAST } from 'utils/constants';
import { getFileExtension } from 'utils/fileHelper';
import { failureToastMessage, genericToastAlert, successToastMessage } from 'utils/toastHelpers';

import React from 'react';

import { useMutation } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';
import PostForm from 'components/PostForm/PostForm';
import { analyzeDocumentFormatting } from 'components/new/Posts/hooks/useEditor/helpers';

import { CREATE_POST_MUTATION } from 'schema/Post/mutations';

interface CreateNewPostProps {
  selectedSpace?: Space;
  t: TFunction;
}

const CreateNewPost = ({ selectedSpace, t }: CreateNewPostProps) => {
  const { community, trackEvent } = useCommunityContext();
  const { spacesList, firstPostableSpace } = useSpacesList();

  const [createPost, { loading: createPostSubmitting }] = useMutation(CREATE_POST_MUTATION);

  const handleSubmit = async (data, { reset, editorRef }) => {
    try {
      const { title, content, spaceId, attachments, mentionedUserIds, body } = data;
      const variables: CreatePostMutationVariables = {
        discussionableId: community.id,
        title,
        body,
        content,
        spaceId,
        attachments: sanitizeAttachments(attachments),
        mentionedUserIds,
      };

      const { data: createPostData, errors: createPostError } = await createPost({
        variables,
        refetchQueries: ['PostList', 'CommunitySpace'],
      });

      const userErrors = createPostData?.createPost?.userErrors;
      if (!userErrors?.length && !createPostError?.length) {
        successToastMessage(t(SUCCESS_TOAST.CREATE_POST));
        sendTrackingEvent(data);

        reset({
          title: '',
          content: '',
          body: {
            jsonContent: null,
            plainText: '',
          },
          spaceId: data.spaceId,
          attachments: [],
          mentionedUserIds: [],
        });
        editorRef.current?.reset();
      } else {
        failureToastMessage(userErrors?.[0]?.message);
      }
    } catch (error) {
      genericToastAlert();
    }
  };

  const sendTrackingEvent = (data) => {
    const {
      ATTACHMENTS,
      POST_TYPE,
      HAS_MENTION,
      NUMBER_OF_ATTACHMENTS,
      FILE_EXTENSIONS,
      HAS_RICH_TEXT,
      FORMATTING_TYPES,
    } = TrackingProperty;

    const formattingTypes = analyzeDocumentFormatting(data.body?.jsonContent);
    trackEvent(TrackingEvent.POST_CREATED, {
      [ATTACHMENTS]: data.attachments.map((attachment) => attachment.attachmentType),
      [FILE_EXTENSIONS]: data.attachments
        ?.map((att) => getFileExtension(att.fileName))
        .filter((ext) => !!ext),
      [NUMBER_OF_ATTACHMENTS]: data.attachments?.length,
      [POST_TYPE]: EVENT_POST_TYPES.POST,
      [HAS_MENTION]: !!data.mentionedUsers?.length,
      [FORMATTING_TYPES]: formattingTypes.formattingTypes,
      [HAS_RICH_TEXT]: formattingTypes.hasRichText,
    });
  };

  return (
    <PostForm
      isSubmitting={createPostSubmitting}
      selectedSpace={
        selectedSpace || {
          id: firstPostableSpace?.value || '',
          name: firstPostableSpace?.label || '',
        }
      }
      showSpaceSelect={!selectedSpace}
      spacesList={spacesList}
      t={t}
      onSubmit={handleSubmit}
    />
  );
};

export default withTranslation()(CreateNewPost);
